import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { STATES, SERVICES } from "@/lib/constants";
import { QICard } from "@/components/site/QICard";

export default function Directory() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [qis, setQis] = useState(null);
  const state = params.get("state") || "";
  const service = params.get("service") || "";

  useEffect(() => {
    setQ(params.get("q") || "");
  }, [params]);

  useEffect(() => {
    setQis(null);
    api.get("/qis", { params: { q: params.get("q") || "", state, service } })
      .then(({ data }) => setQis(data)).catch(() => setQis([]));
  }, [params, state, service]);

  const update = (k, v) => {
    const p = new URLSearchParams(params);
    if (v) p.set(k, v); else p.delete(k);
    setParams(p);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="directory-page">
      <span className="eyebrow">Directory</span>
      <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mt-4">Qualified intermediaries</h1>
      <p className="text-white/55 mt-3 max-w-2xl">Every listing is reviewed before it's published. Filter by the state your property is in and the exchange type you need.</p>

      <div className="mt-10 grid md:grid-cols-12 gap-3">
        <form onSubmit={(e) => { e.preventDefault(); update("q", q); }} className="md:col-span-6 relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search firm, city or keyword" className="field !pl-11 !pr-24" data-testid="directory-search-input" />
          <button className="absolute right-2 top-1/2 -translate-y-1/2 bg-qi-pink text-white text-xs font-bold rounded-full px-4 py-2" data-testid="directory-search-button">Search</button>
        </form>
        <select value={state} onChange={(e) => update("state", e.target.value)} className="field md:col-span-3" data-testid="directory-state-select">
          <option value="" className="bg-ink">All states</option>
          {STATES.map(([c, n]) => <option key={c} value={c} className="bg-ink">{n}</option>)}
        </select>
        <select value={service} onChange={(e) => update("service", e.target.value)} className="field md:col-span-3" data-testid="directory-service-select">
          <option value="" className="bg-ink">All exchange types</option>
          {SERVICES.map((s) => <option key={s} value={s} className="bg-ink">{s}</option>)}
        </select>
      </div>

      {qis === null ? (
        <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-qi-pink" /></div>
      ) : qis.length === 0 ? (
        <div className="glass-card p-12 mt-10 text-center" data-testid="directory-empty">
          <p className="font-display text-xl font-bold">No intermediaries match yet</p>
          <p className="text-white/50 text-sm mt-2">Try another state or clear your filters.</p>
        </div>
      ) : (
        <>
          <p className="font-mono text-xs text-white/40 mt-8" data-testid="directory-count">{qis.length} result{qis.length === 1 ? "" : "s"}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            {qis.map((qi) => <QICard key={qi.profile_id} qi={qi} />)}
          </div>
        </>
      )}
    </div>
  );
}
